import { createSelector } from '@reduxjs/toolkit'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { getLeInstantPostPurchaseItems } from './view/accommodation'
import { getHotelChangeDatesConfig, OrderHotelChangeDatesConfig } from 'components/Common/OrderView/OrderItemSummaries/HotelItemSummary/HotelSummaryModifyBooking/HotelSummaryModifyBookingUtils'
import { getAllOffers } from 'selectors/offerSelectors'
import getPackageFromOffer from 'lib/offer/getPackageFromOffer'
import { getCalenderDaysByCheckIn } from 'lib/offer/offerCalendarUtils'

export const getFlashExtendDatesEnabled = createSelector(
  (state: App.State) => state.optimizely.optimizelyFeatureFlags,
  (optimizelyFeatureFlags): boolean => {
    return !!optimizelyFeatureFlags[OptimizelyFeatureFlags.flashExtendDatesEnabled] // && offer?.type === 'hotel'
  },
)

export const getRequestChangeDatesEnabled = createSelector(
  (state: App.State) => state.optimizely.optimizelyFeatureFlags,
  (optimizelyFeatureFlags): boolean => {
    return !!optimizelyFeatureFlags[OptimizelyFeatureFlags.csxOutsidePolicyChangeDatesRequestEnabled]
  },
)

export const getAccommodationChangeDatesConfig = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => getLeInstantPostPurchaseItems(state),
  (state: App.State) => getAllOffers(state),
  (existingOrder, postPurchaseItems, allOffers): OrderHotelChangeDatesConfig | null => {
    const cartItem = postPurchaseItems[0]
    const orderItem = existingOrder?.items.find(i => i.id === cartItem?.orderItemId)

    const offer = allOffers[cartItem?.offerId] as App.Offer

    if (!orderItem || !offer || !existingOrder) {
      return null
    }

    return getHotelChangeDatesConfig(
      false,
      orderItem,
      existingOrder,
      offer,
    )
  },
)

export const getApprovedOutsidePolicyDatesRequest = createSelector(
  (state: App.State) => state.orders.datesRequests,
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => getLeInstantPostPurchaseItems(state),
  (datesRequests, order, cartItems) => {
    const item = cartItems[0]
    if (!item || !order) return null
    const itemId = item.orderItemId
    const orderId = order?.id

    return datesRequests?.[orderId]?.find(
      r => r.itemId === itemId &&
      ['approved', 'approved_bnbl'].includes(r.status) &&
      r.isOutsidePolicy,
    ) || null
  },
)

/**
 * Check if requested date still available
 */
export const isRequestedDateAvailable = createSelector(
  (state: App.State) => getApprovedOutsidePolicyDatesRequest(state),
  (state: App.State) => getLeInstantPostPurchaseItems(state),
  (state: App.State) => getAllOffers(state),
  (state: App.State) => state.calendar.calendarsByPackageKey,
  (approvedOutsidePolicyDatesRequest, cartItems, allOffers, calendarsByPackageKey) => {
    if (
      !approvedOutsidePolicyDatesRequest
    ) {
      return false
    }

    const cartItem = cartItems[0]
    const offer = allOffers[cartItem?.offerId] as App.Offer
    const pkg = getPackageFromOffer(offer, cartItem.packageId, cartItem.duration)
    const calendar = calendarsByPackageKey[pkg?.uniqueKey ?? '']
    if (!calendar) {
      return false
    }

    const requestDate = approvedOutsidePolicyDatesRequest?.requestDate
    const requestedDay = getCalenderDaysByCheckIn(calendar)[requestDate]

    return requestedDay?.hotelAvailable
  },
)
